import "./PersonalInfo.css";
import { useEffect, useMemo, useState, useRef } from "react";
import ManagerSubHeaderLinks from "../../components/managerSubHeaderLinks/ManagerSubHeaderLinks.jsx";
import { useParams } from "react-router-dom";
import UserShortProfile from "../../components/userShortProfile/UserShortProfile.jsx";
import DefaultProfileImage from "../../medias/images/default.jpg";
import {
  getUserInformations,
  updateUserRequest,
} from "../../services/wisdomServices/userService";
import { requestGetImagesFromBucket } from "../../services/wisdomServices/trackingService";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { timezones } from "../../utils/helper/timezones.js";
import { Toast } from "primereact/toast";
import { Can } from "../../security/Can.jsx";
import { PERMISSIONS } from "../../security/permissions.generated.js";

const DATE_FIELDS = ["birthday", "startWorkDate", "contractEndDate"];

const EDITABLE_FIELDS = [
  "firstName",
  "lastName",
  "pronoun",
  "jobDescription",
  "phone",
  "personalEmail",
  "emergencyContact",
  "birthday",
  "city",
  "address",
  "zipcode",
  "timezone",
];

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const yyyyMmDdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (yyyyMmDdMatch) {
      const [, year, month, day] = yyyyMmDdMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const ddMmYyyyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (ddMmYyyyMatch) {
      const [, day, month, year] = ddMmYyyyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

const formatDateForApi = (value) => {
  const date = parseDateValue(value);

  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normalizeComparableValue = (field, value) => {
  if (DATE_FIELDS.includes(field)) {
    return formatDateForApi(value) ?? "";
  }

  return String(value ?? "");
};

const mapUserToFormData = (userData) => ({
  username: userData?.username ?? "",
  firstName: userData?.firstName ?? "",
  lastName: userData?.lastName ?? "",
  pronoun: userData?.pronoun ?? "",
  jobDescription: userData?.jobDescription ?? "",
  phone: userData?.phone ?? "",
  personalEmail: userData?.personalEmail ?? "",
  emergencyContact: userData?.emergencyContact ?? "",
  birthday: parseDateValue(userData?.birthday),
  city: userData?.city ?? "",
  address: userData?.address ?? "",
  zipcode: userData?.zipcode ?? "",
  timezone: userData?.timezone ?? "",
  startWorkDate: parseDateValue(userData?.startWorkDate),
  contractEndDate: parseDateValue(userData?.contractEndDate),
  contractStatus: userData?.contractStatus ?? "",
});

const getChangedPayload = (formData, originalFormData) => {
  return EDITABLE_FIELDS.reduce((payload, field) => {
    const currentValue = normalizeComparableValue(field, formData?.[field]);
    const originalValue = normalizeComparableValue(field, originalFormData?.[field]);

    if (currentValue !== originalValue) {
      payload[field] = DATE_FIELDS.includes(field)
        ? formatDateForApi(formData?.[field])
        : formData?.[field] ?? "";
    }

    return payload;
  }, {});
};

/**
 * @function UserPersonalInfo
 * @returns {JSX.Element} - user personal information section
 */
function UserPersonalInfo() {
  const params = useParams();
  const toast = useRef(null);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState(null);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const hasChanges = useMemo(() => {
    if (!formData || !originalFormData) return false;

    return EDITABLE_FIELDS.some((field) => {
      const currentValue = normalizeComparableValue(field, formData[field]);
      const originalValue = normalizeComparableValue(field, originalFormData[field]);

      return currentValue !== originalValue;
    });
  }, [formData, originalFormData]);

  const showSuccess = (message) => {
    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: message,
      life: 3000,
    });
  };

  /**
   * @async
   * @function getUser
   * @description - fetches user information by employee ID and resolves profile image
   */
  const getUser = async () => {
    try {
      setLoading(true);

      const userInformations = await getUserInformations(params.employeeId);
      const userData = userInformations;

      if (!userData?.image) {
        userData.image = DefaultProfileImage;
      } else {
        const blob = await requestGetImagesFromBucket(
          userData.id,
          userData.image,
          "profile"
        );

        userData.image = blob ? URL.createObjectURL(blob) : DefaultProfileImage;
      }

      const initialFormData = mapUserToFormData(userData);

      setUser(userData);
      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUser();

    // componentDidMount events
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;

    try {
      setSaving(true);

      const payload = getChangedPayload(formData, originalFormData);

      await updateUserRequest(user.id, payload);

      const updatedUser = {
        ...user,
        ...formData,
      };

      const updatedFormData = mapUserToFormData(updatedUser);

      setUser(updatedUser);
      setFormData(updatedFormData);
      setOriginalFormData(updatedFormData);

      showSuccess("User information updated successfully.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="manager-view-personal-info">
      <Toast ref={toast} position="top-center"/>
      {!loading && user && formData && (
        <div className="summaryMain">
          <div className="my-dashboard-header">
            <UserShortProfile
              employeeName={`${formData.firstName} ${formData.lastName}`}
              employeePronoun={formData.pronoun}
              employeeDesignation={formData.jobDescription}
              employeeID={user.id}
              employeeUsername={formData.username}
              employeeLocation={formData.city}
              employeePicture={user.image}
              employeeDisabled={user.isDisabled}
              employeeContractEndDate={formData.contractEndDate}
              employeeDepartment={user.department}
              employeeIdvStatus={user.trolleyIdvStatus}
              employeePersonalEmail={formData.personalEmail}
              showManagerLinks={true}
            />
          </div>

          <div className="user-info-manager-view-title">
            <h1 className="personal-info-header">Details</h1>
            <Can permission={PERMISSIONS.usersById_patch}>
              <Button
                label="Save"
                icon="pi pi-save"
                severity="success"
                loading={saving}
                disabled={!hasChanges || saving}
                onClick={handleSave}
              />

            </Can>

          </div>

          <div>
            {/* Group 1 */}
            <div className="user-informations-form-group">
              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-first-name">First name</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-user"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-first-name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-last-name">Last name</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-user"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-last-name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-pronoun">
                  Preferred pronoun
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-bars"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-pronoun"
                    value={formData.pronoun}
                    onChange={(e) =>
                      handleInputChange("pronoun", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-job-title">Job title</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-briefcase"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-job-title"
                    value={formData.jobDescription}
                    onChange={(e) =>
                      handleInputChange("jobDescription", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Group 2 */}
            <div className="user-informations-form-group">
              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-phone">Phone</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-phone"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-personal-email">
                  Personal email
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-envelope"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-personal-email"
                    value={formData.personalEmail}
                    onChange={(e) =>
                      handleInputChange("personalEmail", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-emergency-contact">
                  Emergency contact
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-phone"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-emergency-contact"
                    value={formData.emergencyContact}
                    onChange={(e) =>
                      handleInputChange("emergencyContact", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-birthday">Birthday</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-calendar"></i>
                  </span>
                  <Calendar
                    id="user-profile-infos-birthday"
                    value={formData.birthday}
                    onChange={(e) => handleInputChange("birthday", e.value)}
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/yyyy"
                    className="w-full"
                    showIcon
                  />
                </div>
              </div>
            </div>

            {/* Group 3 */}
            <div className="user-informations-form-group">
              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-city">City/Country</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-map"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-city"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-address">Address</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-map-marker"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-address"
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-zip-code">ZIP code</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-info-circle"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-zip-code"
                    value={formData.zipcode}
                    onChange={(e) =>
                      handleInputChange("zipcode", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1 user-profile-infos-timezone">
                <label htmlFor="timezone-select">User timezone</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-clock"></i>
                  </span>
                  <Dropdown
                    id="timezone-select"
                    options={timezones}
                    value={formData.timezone}
                    onChange={(e) => handleInputChange("timezone", e.value)}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Group 4 */}
            <div className="user-informations-form-group">
              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-username">Email</label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-envelope"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-username"
                    readOnly
                    disabled
                    value={formData.username}
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-date-joined">
                  Date joined
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-calendar-clock"></i>
                  </span>
                  <Calendar
                    id="user-profile-infos-date-joined"
                    value={formData.startWorkDate}
                    readOnly
                    disabled
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/yyyy"
                    className="w-full"
                    showIcon
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-contract-expire-date">
                  Contract expire date
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-calendar"></i>
                  </span>
                  <Calendar
                    id="user-profile-infos-contract-expire-date"
                    value={formData.contractEndDate}
                    readOnly
                    disabled
                    dateFormat="dd/mm/yy"
                    placeholder="dd/mm/yyyy"
                    className="w-full"
                    showIcon
                  />
                </div>
              </div>

              <div className="p-inputgroup flex-column flex-1">
                <label htmlFor="user-profile-infos-contract-status">
                  Contract status
                </label>
                <div className="p-inputgroup">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-exclamation-circle"></i>
                  </span>
                  <InputText
                    id="user-profile-infos-contract-status"
                    value={formData.contractStatus}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserPersonalInfo;